import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Heading, Icon, Spinner, Text } from "@telia/teddy";
import {
    API_URL_NOTIFICATIONS,
    API_URL_NOTIFICATIONS_UNREAD_COUNT,
    API_URL_NOTIFICATIONS_READ_ALL,
    API_URL_NOTIFICATION_READ,
} from "../../../api";
import { retailerFetch } from "../../../retailerFetch";
import type { NotificationEntry } from "../../../views/Feedbacks/notificationTypes";
import PAGES from "../../../../../../constants/pages";
import "./NotificationBell.less";

const formatTime = (iso: string) =>
    new Date(iso).toLocaleString("nb-NO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

const NotificationBell: React.FC = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState<NotificationEntry[]>([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await retailerFetch(API_URL_NOTIFICATIONS_UNREAD_COUNT);
            if (res.ok) {
                const { count } = await res.json();
                setUnreadCount(count);
            }
        } catch {
            // Silently ignore
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await retailerFetch(API_URL_NOTIFICATIONS);
            if (res.ok) {
                const data: NotificationEntry[] = await res.json();
                setNotifications(data);
            }
        } catch {
            // Silently ignore
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnreadCount();
    }, [fetchUnreadCount]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    const handleToggle = () => {
        const willOpen = !open;
        setOpen(willOpen);
        if (willOpen) {
            fetchNotifications();
        }
    };

    const handleNotificationClick = async (notification: NotificationEntry) => {
        if (!notification.read) {
            retailerFetch(API_URL_NOTIFICATION_READ(notification.id), { method: "PUT" }).catch(() => {});
            setUnreadCount((c) => Math.max(0, c - 1));
            setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
        }
        setOpen(false);
        navigate(`${PAGES.RETAILER.FEEDBACK_DETAILS.URL}${notification.feedbackId}`);
    };

    const handleMarkAllAsRead = async () => {
        await retailerFetch(API_URL_NOTIFICATIONS_READ_ALL, { method: "PUT" });
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    return (
        <div ref={dropdownRef} style={{ position: "relative" }}>
            <button className="notification-bell" onClick={handleToggle} aria-label="Varsler">
                <Icon name="alarm-on" size="md" opacity={unreadCount > 0 ? 1 : 0.4} />
                {unreadCount > 0 && (
                    <span className="notification-bell__badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
            </button>

            {open && (
                <div className="notification-bell__dropdown">
                    <div className="notification-bell__header">
                        <Heading mb="100" as="h3" variant="title-100">
                            Varsler
                        </Heading>
                        {unreadCount > 0 && (
                            <Button variant="text" size="sm" onClick={handleMarkAllAsRead}>
                                Merk alle som lest
                            </Button>
                        )}
                    </div>
                    <div className="notification-bell__list">
                        {loading && notifications.length === 0 && (
                            <div className="notification-bell__empty">
                                <Spinner />
                            </div>
                        )}
                        {!loading && notifications.length === 0 && (
                            <div className="notification-bell__empty">
                                <Text>Ingen varsler</Text>
                            </div>
                        )}
                        {notifications.map((n) => (
                            <button
                                key={n.id}
                                className={`notification-bell__item ${!n.read ? "notification-bell__item--unread" : ""}`}
                                onClick={() => handleNotificationClick(n)}
                            >
                                <Text variant="paragraph-100-bold">
                                    <strong>{n.title}</strong>
                                </Text>
                                <Text variant="paragraph-100">{n.body}</Text>
                                <div className="notification-bell__item-time">{formatTime(n.createdAt)}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
